import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  getIndividual, onIndividual, onIndividualNotes, onIndividualAlerts,
  updateIndividual, deactivateIndividual,
} from "../services/individualService";
import { getEnabledServices, getBillingConfig, getShortCode } from "../services/billingConfigService";
import {
  ArrowLeft, FileText, Shield, AlertTriangle, Clock, Pill,
  MapPin, Phone, User, Edit3, Mic, Download, ChevronRight,
  Calendar, Activity, Heart, CheckCircle, Target, ClipboardList,
} from "lucide-react";
import TreatmentPlanTab from "../components/TreatmentPlanTab";
import IntakeInfoTab from "../components/IntakeInfoTab";
import ConsentsTab from "../components/ConsentsTab";
import DiagnosisMedTab from "../components/DiagnosisMedTab";
import AuthorizationTab from "../components/AuthorizationTab";
import AssessmentTab from "../components/AssessmentTab";

export default function IndividualProfile() {
  const navigate = useNavigate();
  const { individualId } = useParams();
  const location = useLocation();
  const { user, profile } = useAuth();
  const justCreated = location.state?.justCreated;

  const [individual, setIndividual] = useState(null);
  const [notes, setNotes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeSection, setActiveSection] = useState("timeline");
  const [loading, setLoading] = useState(true);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [billingConfig, setBillingConfig] = useState(null);
  const [reviewingAlertId, setReviewingAlertId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewAction, setReviewAction] = useState(null); // "review" or "resolve"
  const [reviewSaving, setReviewSaving] = useState(false);

  // Load billing config for service picker
  useEffect(() => {
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    getBillingConfig(org).then(config => {
      setBillingConfig(config);
      setServiceOptions(getEnabledServices(config));
    });
  }, [profile]);
  const [showExportPin, setShowExportPin] = useState(false);
  const [exportPinVal, setExportPinVal] = useState("");
  const [exportPinError, setExportPinError] = useState("");

  // PDF Export: generates a printable clinical record and opens print dialog
  const exportProfilePDF = (ind, allNotes, allAlerts) => {
    const formatDate = (d) => {
      if (!d) return "—";
      if (d.toDate) return d.toDate().toLocaleDateString();
      return new Date(d).toLocaleDateString();
    };

    const noteRows = allNotes.map(n => {
      const date = formatDate(n.createdAt || n.date);
      const svc = n.serviceType || n.noteData?.header?.serviceType || "ICM Visit";
      const status = n.status || "submitted";
      const content = n.noteData?.sections?.intervention?.content
        || n.noteData?.sections?.response?.content
        || n.content?.sections?.intervention?.content
        || "See full note in system.";
      return `<tr>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${date}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${svc}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${status}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${(content || "").substring(0, 200)}${content && content.length > 200 ? "..." : ""}</td>
      </tr>`;
    }).join("");

    const alertRows = allAlerts.map(a => {
      const date = formatDate(a.createdAt);
      return `<tr>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${date}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;color:#c53030;font-weight:bold">${a.category || "Safety Concern"}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${a.severity || "—"}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;font-style:italic">"${a.triggerPhrase}"</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${a.status}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html><head><title>Clinical Record - ${ind.lastName}, ${ind.firstName}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1a202c; margin: 40px; }
  h1 { font-size: 20px; color: #1a365d; border-bottom: 2px solid #2b6cb0; padding-bottom: 8px; }
  h2 { font-size: 14px; color: #2b6cb0; margin-top: 24px; margin-bottom: 8px; }
  .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
  .header-left { font-size: 10px; color: #718096; }
  .field { margin-bottom: 4px; }
  .label { font-weight: bold; color: #4a5568; display: inline-block; width: 160px; }
  .risk-high { color: #c53030; font-weight: bold; }
  .risk-moderate { color: #c05621; font-weight: bold; }
  .risk-low { color: #276749; font-weight: bold; }
  table { border-collapse: collapse; width: 100%; margin-top: 8px; }
  th { background: #edf2f7; padding: 6px 8px; border: 1px solid #ddd; font-size: 11px; text-align: left; }
  .confidential { text-align: center; color: #c53030; font-weight: bold; font-size: 10px; margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; }
  @media print { body { margin: 20px; } }
</style>
</head><body>
<div class="header">
  <div><h1>Clinical Record</h1></div>
  <div class="header-left">
    Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}<br/>
    Organization: ${ind.organization || "—"}<br/>
    Record ID: ${ind.id}
  </div>
</div>

<h2>Demographics</h2>
<div class="field"><span class="label">Name:</span> ${ind.lastName}, ${ind.firstName}</div>
<div class="field"><span class="label">Date of Birth:</span> ${ind.dob || "—"}</div>
<div class="field"><span class="label">Gender:</span> ${ind.gender || "—"}</div>
<div class="field"><span class="label">Phone:</span> ${ind.phone || "—"}</div>
<div class="field"><span class="label">Address:</span> ${ind.address || "—"}</div>
<div class="field"><span class="label">County:</span> ${ind.county || "—"}</div>

<h2>Program Information</h2>
<div class="field"><span class="label">Program:</span> ${ind.program || "—"}</div>
<div class="field"><span class="label">Service Type:</span> ${ind.serviceType || "—"}</div>
<div class="field"><span class="label">Admission Date:</span> ${ind.admissionDate || "—"}</div>
<div class="field"><span class="label">Contact Frequency:</span> ${ind.contactFrequency || "—"}</div>
<div class="field"><span class="label">Primary Agent:</span> ${ind.primaryAgentName || "—"}</div>
<div class="field"><span class="label">Risk Level:</span> <span class="risk-${ind.riskLevel}">${(ind.riskLevel || "low").toUpperCase()}</span></div>

<h2>Clinical Information</h2>
<div class="field"><span class="label">Diagnoses:</span> ${(ind.diagnoses || []).join(", ") || "None documented"}</div>
<div class="field"><span class="label">Medications:</span> ${(ind.medications || []).join(", ") || "None documented"}</div>
<div class="field"><span class="label">Allergies:</span> ${(ind.allergies || []).join(", ") || "None documented"}</div>

<h2>Insurance</h2>
<div class="field"><span class="label">Medicaid ID:</span> ${ind.medicaidId || "—"}</div>
<div class="field"><span class="label">Insurance Provider:</span> ${ind.insuranceProvider || "—"}</div>
<div class="field"><span class="label">Policy ID:</span> ${ind.insurancePolicyId || "—"}</div>

<h2>Emergency Contact</h2>
<div class="field"><span class="label">Name:</span> ${ind.emergencyContact || "—"}</div>
<div class="field"><span class="label">Phone:</span> ${ind.emergencyPhone || "—"}</div>
<div class="field"><span class="label">Relationship:</span> ${ind.emergencyRelation || "—"}</div>

${ind.specialConsiderations ? `<h2>Special Considerations</h2><p>${ind.specialConsiderations}</p>` : ""}
${ind.onboardingNotes ? `<h2>Onboarding Notes</h2><p>${ind.onboardingNotes}</p>` : ""}

<h2>Contact Timeline (${allNotes.length} Notes)</h2>
${allNotes.length > 0 ? `<table>
  <tr><th>Date</th><th>Service Type</th><th>Status</th><th>Summary</th></tr>
  ${noteRows}
</table>` : "<p>No notes on file.</p>"}

<h2>Safety Alert History (${allAlerts.length} Alerts)</h2>
${allAlerts.length > 0 ? `<table>
  <tr><th>Date</th><th>Category</th><th>Severity</th><th>Trigger</th><th>Status</th></tr>
  ${alertRows}
</table>` : "<p>No safety alerts on file.</p>"}

<div class="confidential">
  CONFIDENTIAL — PROTECTED HEALTH INFORMATION (PHI)<br/>
  This document contains information protected under HIPAA. Unauthorized disclosure is prohibited.<br/>
  Printed by: ${profile?.displayName || user?.email || "Unknown"} on ${new Date().toLocaleString()}
</div>
</body></html>`;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Load individual data
  useEffect(() => {
    if (!individualId) return;

    const unsubInd = onIndividual(individualId, (data) => {
      setIndividual(data);
      setLoading(false);
    });

    const unsubNotes = onIndividualNotes(individualId, setNotes);
    const unsubAlerts = onIndividualAlerts(individualId, setAlerts);

    return () => {
      unsubInd();
      unsubNotes();
      unsubAlerts();
    };
  }, [individualId]);

  // Also try loading from localStorage if Firestore is empty
  useEffect(() => {
    if (!loading && !individual) {
      try {
        const localData = JSON.parse(localStorage.getItem("fieldnotes_local_data") || "{}");
        const localInd = (localData.individuals || []).find(i => i.id === individualId);
        if (localInd) setIndividual(localInd);

        // Match local notes by name
        if (localInd) {
          const localNotes = (localData.notes || []).filter(n =>
            (n.individualName || "").toLowerCase().includes((localInd.lastName || "").toLowerCase())
          );
          if (localNotes.length > 0 && notes.length === 0) setNotes(localNotes);
        }
      } catch (e) { /* ok */ }
    }
  }, [loading, individual, individualId, notes.length]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(99,179,237,0.1)", borderTopColor: "#63b3ed", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!individual) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <User size={40} style={{ color: "#2d3748", marginBottom: 12 }} />
        <div style={{ fontSize: 16, fontWeight: 800, color: "#4a5568" }}>Individual Not Found</div>
        <button onClick={() => navigate("/caseload")} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 12, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white", border: "none", cursor: "pointer" }}>
          Back to Caseload
        </button>
      </div>
    );
  }

  const ind = individual;
  const riskColors = { high: "#fc8181", moderate: "#ecc94b", low: "#68d391" };
  const submittedNotes = notes.filter(n => n.status === "submitted" || n.status === "signed");
  const activeAlerts = alerts.filter(a => a.status !== "resolved");

  const sections = [
    { key: "timeline", label: "Timeline", icon: Clock },
    { key: "intake", label: "Intake", icon: ClipboardList },
    { key: "consents", label: "Consents", icon: Shield },
    { key: "treatment", label: "Tx Plan", icon: Target, badge: individual?.treatmentPlan?.goals?.length ? 0 : 1 },
    { key: "clinical", label: "Clinical", icon: Heart },
    { key: "assessments", label: "Assess", icon: Activity },
    { key: "auths", label: "Auths", icon: Shield },
    { key: "safety", label: "Safety", icon: Shield, badge: activeAlerts.length },
    { key: "details", label: "Details", icon: User },
  ];

  const S = {
    card: { background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", borderRadius: 16, padding: 16, marginBottom: 12 },
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Header */}
      <header style={{
        padding: "16px 20px", background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(99,179,237,0.1)", position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate("/caseload")} style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{ind.lastName}, {ind.firstName}</div>
              <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                DOB: {ind.dob} · {ind.program}
                {ind.primaryAgentName && <span> · Agent: {ind.primaryAgentName}</span>}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 800, textTransform: "uppercase", padding: "4px 10px", borderRadius: 20,
              background: `${riskColors[ind.riskLevel] || "#68d391"}15`,
              color: riskColors[ind.riskLevel] || "#68d391",
            }}>{ind.riskLevel} risk</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
          {[
            { label: "Notes", value: notes.length, color: "#4fd1c5" },
            { label: "Alerts", value: alerts.length, color: alerts.length > 0 ? "#fc8181" : "#68d391" },
            { label: "Last Contact", value: ind.lastContactDate ? new Date(ind.lastContactDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—", color: "#63b3ed" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </header>

      {/* Just Created Banner */}
      {justCreated && (
        <div style={{ padding: "10px 20px", background: "rgba(104,211,145,0.06)", borderBottom: "1px solid rgba(104,211,145,0.1)", display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={14} style={{ color: "#68d391" }} />
          <span style={{ fontSize: 12, color: "#68d391", fontWeight: 700 }}>Individual added to your caseload</span>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ padding: "12px 20px", display: "flex", gap: 8 }}>
        <button onClick={() => setShowServicePicker(true)} style={{
          flex: 1, padding: "10px 0", borderRadius: 12, fontSize: 12, fontWeight: 800,
          background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white",
          border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <Mic size={14} /> Start Session
        </button>
        <button onClick={() => navigate("/onboarding", { state: { mode: "update", individual: ind } })} style={{
          flex: 1, padding: "10px 0", borderRadius: 12, fontSize: 12, fontWeight: 700,
          background: "rgba(99,179,237,0.08)", color: "#63b3ed",
          border: "1px solid rgba(99,179,237,0.15)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <Edit3 size={14} /> Update Record
        </button>
        <button onClick={() => setShowExportPin(true)} style={{
          padding: "10px 14px", borderRadius: 12, fontSize: 12, fontWeight: 700,
          background: "rgba(183,148,244,0.08)", color: "#b794f4",
          border: "1px solid rgba(183,148,244,0.15)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <Download size={14} />
        </button>
      </div>

      {/* Export PIN Modal */}
      {showExportPin && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", padding: 20 }}
          onClick={() => { setShowExportPin(false); setExportPinVal(""); setExportPinError(""); }}>
          <div style={{ background: "linear-gradient(135deg, #0f1729, #162033)", border: "1px solid rgba(183,148,244,0.15)", borderRadius: 16, padding: 24, maxWidth: 360, width: "100%" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#b794f4", marginBottom: 8 }}>Export Clinical Record</div>
            <div style={{ fontSize: 12, color: "#a0aec0", lineHeight: 1.6, marginBottom: 16 }}>
              This will generate a printable PDF containing protected health information for <strong style={{ color: "#e2e8f0" }}>{ind.firstName} {ind.lastName}</strong>. Enter your admin PIN to continue.
            </div>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="Enter PIN"
              value={exportPinVal}
              onChange={e => { setExportPinVal(e.target.value.replace(/\D/g, "")); setExportPinError(""); }}
              autoFocus
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 12, fontSize: 18, fontWeight: 800,
                textAlign: "center", letterSpacing: 8,
                background: "rgba(15,23,42,0.5)", border: `1px solid ${exportPinError ? "rgba(252,129,129,0.4)" : "rgba(99,179,237,0.08)"}`,
                color: "#e2e8f0", outline: "none", marginBottom: 8,
              }}
              onKeyDown={e => { if (e.key === "Enter") document.getElementById("export-btn")?.click(); }}
            />
            {exportPinError && (
              <div style={{ fontSize: 11, color: "#fc8181", marginBottom: 8, textAlign: "center" }}>{exportPinError}</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button id="export-btn" onClick={() => {
                const storedPin = localStorage.getItem("fieldnotes_admin_pin") || "1234";
                if (exportPinVal !== storedPin) {
                  setExportPinError("Incorrect PIN");
                  return;
                }
                setShowExportPin(false);
                setExportPinVal("");
                setExportPinError("");
                exportProfilePDF(ind, notes, alerts);
              }} style={{
                flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 800,
                background: "linear-gradient(135deg, #805ad5, #6b46c1)", color: "white", border: "none", cursor: "pointer",
              }}>Export PDF</button>
              <button onClick={() => { setShowExportPin(false); setExportPinVal(""); setExportPinError(""); }} style={{
                flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 600,
                background: "transparent", color: "#718096", border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer",
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "0 20px 12px", overflowX: "auto" }}>
        {sections.map(sec => (
          <button key={sec.key} onClick={() => setActiveSection(sec.key)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 10,
            background: activeSection === sec.key ? "rgba(99,179,237,0.12)" : "transparent",
            color: activeSection === sec.key ? "#63b3ed" : "#718096",
            border: `1px solid ${activeSection === sec.key ? "rgba(99,179,237,0.2)" : "transparent"}`,
            fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
          }}>
            <sec.icon size={12} /> {sec.label}
            {(sec.badge || 0) > 0 && (
              <span style={{ background: "#fc8181", color: "#0a0f1e", fontSize: 8, fontWeight: 900, padding: "1px 5px", borderRadius: 20 }}>{sec.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 20px 24px" }}>

        {/* ===== TIMELINE ===== */}
        {activeSection === "timeline" && (
          <div>
            {notes.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0" }}>
                <FileText size={32} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
                <div style={{ fontSize: 13, color: "#4a5568" }}>No sessions recorded yet</div>
                <div style={{ fontSize: 11, color: "#2d3748", marginTop: 4 }}>Start a session to build the clinical timeline.</div>
              </div>
            ) : (
              notes.map((note, i) => (
                <div key={note.id || i} style={{
                  ...S.card, cursor: "pointer",
                  borderLeft: `3px solid ${note.hasPriorityFlags ? "#fc8181" : note.status === "signed" ? "#68d391" : "#4fd1c5"}`,
                }} onClick={() => note.noteData && navigate("/session/review", { state: { note: note.noteData, readOnly: true } })}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{note.serviceType || "Session"}</span>
                        {note.hasPriorityFlags && <AlertTriangle size={11} style={{ color: "#fc8181" }} />}
                      </div>
                      <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
                        {note.agentName ? `${note.agentName} · ` : ""}{note.date} · {note.duration}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                      background: note.status === "signed" ? "rgba(104,211,145,0.1)" : note.status === "submitted" ? "rgba(236,201,75,0.1)" : "rgba(99,179,237,0.08)",
                      color: note.status === "signed" ? "#68d391" : note.status === "submitted" ? "#ecc94b" : "#718096",
                    }}>
                      {note.status === "signed" ? "Signed" : note.status === "submitted" ? "Submitted" : "Draft"}
                    </span>
                  </div>
                  {note.noteData?.presenting && (
                    <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 8, lineHeight: 1.5 }}>
                      {note.noteData.presenting.substring(0, 150)}{note.noteData.presenting.length > 150 ? "..." : ""}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== INTAKE INFO ===== */}
        {activeSection === "intake" && (
          <IntakeInfoTab individual={ind} />
        )}

        {/* ===== CONSENTS ===== */}
        {activeSection === "consents" && (
          <ConsentsTab individualId={ind.id} individualName={`${ind.firstName} ${ind.lastName}`} />
        )}

        {/* ===== TREATMENT PLAN ===== */}
        {activeSection === "treatment" && (
          <TreatmentPlanTab individual={individual} notes={notes} S={S} role={profile?.role} />
        )}

        {/* ===== CLINICAL (DiagnosisMedTab) ===== */}
        {activeSection === "clinical" && (
          <div>
            <DiagnosisMedTab individual={ind} onUpdate={() => {
              getIndividual(individualId).then(data => { if (data) setIndividual(data); });
            }} />

            {/* Risk Level */}
            <div style={S.card}>
              <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Risk Level</div>
              <span style={{
                fontSize: 14, fontWeight: 800, padding: "4px 14px", borderRadius: 10,
                background: `${riskColors[ind.riskLevel] || "#68d391"}15`,
                color: riskColors[ind.riskLevel] || "#68d391",
              }}>{(ind.riskLevel || "low").toUpperCase()}</span>
            </div>
          </div>
        )}

        {/* ===== ASSESSMENTS ===== */}
        {activeSection === "assessments" && (
          <AssessmentTab individual={ind} />
        )}

        {/* ===== AUTHORIZATIONS ===== */}
        {activeSection === "auths" && (
          <AuthorizationTab individual={ind} />
        )}

        {/* ===== SAFETY ===== */}
        {activeSection === "safety" && (
          <div>
            {/* Stats */}
            {alerts.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
                {[
                  { label: "Active", value: alerts.filter(a => a.status === "active" || a.status === "acknowledged").length, color: "#fc8181" },
                  { label: "Reviewed", value: alerts.filter(a => a.status === "reviewed").length, color: "#ecc94b" },
                  { label: "Resolved", value: alerts.filter(a => a.status === "resolved").length, color: "#68d391" },
                ].map((s, i) => (
                  <div key={i} style={{ ...S.card, textAlign: "center", padding: 10, marginBottom: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                    <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {alerts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0" }}>
                <Shield size={32} style={{ color: "rgba(104,211,145,0.2)", margin: "0 auto 8px" }} />
                <div style={{ fontSize: 13, color: "#4a5568" }}>No safety alerts on record</div>
                <div style={{ fontSize: 11, color: "#2d3748", marginTop: 4 }}>Safety alerts are auto-generated from session recordings</div>
              </div>
            ) : (
              alerts.map(alert => {
                const statusColor = alert.status === "resolved" ? "#68d391" : alert.status === "reviewed" ? "#ecc94b" : alert.status === "acknowledged" ? "#f6ad55" : "#fc8181";
                const isSup = profile?.role === "supervisor" || profile?.role === "admin";
                const date = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleDateString() : "";
                const time = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

                return (
                  <div key={alert.id} style={{
                    ...S.card,
                    borderLeft: `3px solid ${statusColor}`,
                  }}>
                    {/* Header badges */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                      {alert.severity && (
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase",
                          background: alert.severity === "critical" ? "rgba(252,129,129,0.15)" : alert.severity === "high" ? "rgba(246,173,85,0.15)" : "rgba(236,201,75,0.15)",
                          color: alert.severity === "critical" ? "#fc8181" : alert.severity === "high" ? "#f6ad55" : "#ecc94b",
                        }}>{alert.severity}</span>
                      )}
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6,
                        background: "rgba(99,179,237,0.1)", color: "#63b3ed", textTransform: "uppercase",
                      }}>{alert.category || "Safety"}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                        background: `${statusColor}15`, color: statusColor, textTransform: "uppercase",
                      }}>{alert.status}</span>
                    </div>

                    {/* Trigger phrase */}
                    <div style={{ fontSize: 14, fontWeight: 800, fontStyle: "italic", color: "#fc8181", marginBottom: 4 }}>
                      "{alert.triggerPhrase}"
                    </div>

                    {/* Meta */}
                    <div style={{ fontSize: 10, color: "#718096" }}>
                      Agent: {alert.agentName} · {date} at {time}
                      {alert.agentLocation && ` · ${alert.agentLocation}`}
                    </div>

                    {/* Transcript snippet */}
                    {alert.transcriptSnippet && (
                      <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.04)" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Transcript Context</div>
                        <div style={{ fontSize: 11, color: "#a0aec0", fontStyle: "italic", lineHeight: 1.5 }}>
                          ...{alert.transcriptSnippet.substring(0, 200)}...
                        </div>
                      </div>
                    )}

                    {/* Review history */}
                    {alert.acknowledgedBy && (
                      <div style={{ fontSize: 10, color: "#f6ad55", marginTop: 6 }}>
                        Acknowledged by {alert.acknowledgedBy} · {alert.acknowledgedAt?.toDate ? alert.acknowledgedAt.toDate().toLocaleString() : ""}
                      </div>
                    )}
                    {alert.reviewedBy && (
                      <div style={{ fontSize: 10, color: "#ecc94b", marginTop: 4 }}>
                        Reviewed by {alert.reviewedBy} · {alert.reviewedAt?.toDate ? alert.reviewedAt.toDate().toLocaleString() : ""}
                      </div>
                    )}
                    {alert.reviewNotes && (
                      <div style={{ marginTop: 4, padding: "6px 10px", borderRadius: 6, background: "rgba(236,201,75,0.04)", border: "1px solid rgba(236,201,75,0.08)" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#ecc94b", marginBottom: 2 }}>Review Notes</div>
                        <div style={{ fontSize: 11, color: "#a0aec0", fontStyle: "italic" }}>{alert.reviewNotes}</div>
                      </div>
                    )}
                    {alert.resolvedBy && (
                      <div style={{ fontSize: 10, color: "#68d391", marginTop: 4 }}>
                        Resolved by {alert.resolvedBy} · {alert.resolvedAt?.toDate ? alert.resolvedAt.toDate().toLocaleString() : ""}
                      </div>
                    )}
                    {alert.resolutionNotes && (
                      <div style={{ marginTop: 4, padding: "6px 10px", borderRadius: 6, background: "rgba(104,211,145,0.04)", border: "1px solid rgba(104,211,145,0.08)" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#68d391", marginBottom: 2 }}>Resolution</div>
                        <div style={{ fontSize: 11, color: "#a0aec0" }}>{alert.resolutionNotes}</div>
                      </div>
                    )}

                    {/* Linked session note */}
                    {alert.noteId && (
                      <button onClick={() => navigate("/session/review", { state: { note: alert.noteData, readOnly: true } })} style={{
                        display: "flex", alignItems: "center", gap: 4, marginTop: 8,
                        fontSize: 10, fontWeight: 700, color: "#63b3ed",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                      }}>
                        <FileText size={10} /> View Session Note
                      </button>
                    )}

                    {/* Supervisor action buttons */}
                    {isSup && alert.status !== "resolved" && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(99,179,237,0.04)" }}>
                        {reviewingAlertId !== alert.id ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            {alert.status === "active" && (
                              <button onClick={async () => {
                                const { acknowledgeAlert } = await import("../services/safetyAlertService");
                                await acknowledgeAlert(alert.id, profile?.displayName || "Supervisor");
                              }} style={{
                                padding: "6px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                                background: "linear-gradient(135deg, #dd6b20, #c05621)", color: "white", border: "none", cursor: "pointer",
                              }}>Acknowledge</button>
                            )}
                            <button onClick={() => { setReviewingAlertId(alert.id); setReviewAction("review"); setReviewNotes(""); }} style={{
                              padding: "6px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                              background: "rgba(236,201,75,0.08)", color: "#ecc94b",
                              border: "1px solid rgba(236,201,75,0.12)", cursor: "pointer",
                            }}>Review</button>
                            <button onClick={() => { setReviewingAlertId(alert.id); setReviewAction("resolve"); setReviewNotes(""); }} style={{
                              padding: "6px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                              background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer",
                            }}>Resolve</button>
                          </div>
                        ) : (
                          /* Inline review panel */
                          <div style={{ padding: 12, borderRadius: 10, background: "rgba(15,23,42,0.5)", border: `1px solid ${reviewAction === "resolve" ? "rgba(104,211,145,0.15)" : "rgba(236,201,75,0.15)"}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: reviewAction === "resolve" ? "#68d391" : "#ecc94b" }}>
                                {reviewAction === "resolve" ? "Resolve Alert" : "Review Alert"}
                              </span>
                              <button onClick={() => setReviewingAlertId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096", fontSize: 10 }}>
                                Cancel
                              </button>
                            </div>

                            {/* Quick action tags */}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                              {[
                                "Contacted agent",
                                "Safety plan updated",
                                "Crisis team notified",
                                "Family contacted",
                                "No immediate risk",
                                "Mandatory report filed",
                              ].map(tag => (
                                <button key={tag} onClick={() => setReviewNotes(prev => prev ? `${prev}. ${tag}` : tag)} style={{
                                  padding: "3px 8px", borderRadius: 6, fontSize: 9, fontWeight: 600, cursor: "pointer",
                                  background: "rgba(99,179,237,0.05)", color: "#718096",
                                  border: "1px solid rgba(99,179,237,0.06)",
                                }}>{tag}</button>
                              ))}
                            </div>

                            <textarea
                              style={{
                                width: "100%", padding: "8px 10px", borderRadius: 8,
                                background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
                                color: "#e2e8f0", fontSize: 11, outline: "none", resize: "vertical", minHeight: 60,
                              }}
                              placeholder={reviewAction === "resolve" ? "Resolution notes — what was done to address this concern..." : "Review notes — actions taken, observations, follow-up needed..."}
                              value={reviewNotes}
                              onChange={e => setReviewNotes(e.target.value)}
                            />

                            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                              <button onClick={() => setReviewingAlertId(null)} style={{
                                flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 700,
                                background: "rgba(99,179,237,0.06)", color: "#718096",
                                border: "1px solid rgba(99,179,237,0.06)", cursor: "pointer",
                              }}>Cancel</button>
                              <button
                                disabled={reviewSaving || (reviewAction === "resolve" && !reviewNotes.trim())}
                                onClick={async () => {
                                  setReviewSaving(true);
                                  if (reviewAction === "resolve") {
                                    const { resolveAlert } = await import("../services/safetyAlertService");
                                    await resolveAlert(alert.id, profile?.displayName || "Supervisor", reviewNotes);
                                  } else {
                                    const { reviewAlert } = await import("../services/safetyAlertService");
                                    await reviewAlert(alert.id, profile?.displayName || "Supervisor", reviewNotes);
                                  }
                                  setReviewSaving(false);
                                  setReviewingAlertId(null);
                                  setReviewNotes("");
                                }}
                                style={{
                                  flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 700,
                                  background: reviewAction === "resolve" ? "linear-gradient(135deg, #38a169, #2f855a)" : "linear-gradient(135deg, #dd6b20, #c05621)",
                                  color: "white", border: "none", cursor: "pointer",
                                  opacity: reviewSaving || (reviewAction === "resolve" && !reviewNotes.trim()) ? 0.5 : 1,
                                }}>
                                {reviewSaving ? "Saving..." : reviewAction === "resolve" ? "Resolve Alert" : "Mark Reviewed"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ===== DETAILS ===== */}
        {activeSection === "details" && (
          <div>
            {[
              { label: "Full Name", value: `${ind.firstName} ${ind.lastName}` },
              { label: "Date of Birth", value: ind.dob },
              { label: "Gender", value: ind.gender },
              { label: "Phone", value: ind.phone, link: ind.phone ? `tel:${ind.phone}` : null },
              { label: "Email", value: ind.email },
              { label: "Address", value: ind.address },
              { label: "County", value: ind.county },
              { label: "Program", value: ind.program },
              { label: "Contact Frequency", value: ind.contactFrequency },
              { label: "Medicaid ID", value: ind.medicaidId },
              { label: "Insurance", value: ind.insuranceProvider },
              { label: "Admission Date", value: ind.admissionDate },
              { label: "Emergency Contact", value: ind.emergencyContact },
              { label: "Emergency Phone", value: ind.emergencyPhone, link: ind.emergencyPhone ? `tel:${ind.emergencyPhone}` : null },
              { label: "Relationship", value: ind.emergencyRelation },
              { label: "Primary Agent", value: ind.primaryAgentName },
              { label: "Assigned Agents", value: (ind.assignedAgentNames || []).join(", ") },
              { label: "Notes", value: ind.onboardingNotes },
              { label: "Special Considerations", value: ind.specialConsiderations },
            ].filter(f => f.value).map((field, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid rgba(99,179,237,0.04)",
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, minWidth: 120 }}>
                  {field.label}
                </div>
                {field.link ? (
                  <a href={field.link} style={{ fontSize: 13, color: "#63b3ed", textDecoration: "none" }}>{field.value}</a>
                ) : (
                  <div style={{ fontSize: 13, color: "#e2e8f0", textAlign: "right", flex: 1 }}>{field.value}</div>
                )}
              </div>
            ))}

            {/* Deactivate */}
            <button onClick={async () => {
              if (window.confirm(`Deactivate ${ind.firstName} ${ind.lastName}? They will be removed from active caseload.`)) {
                await deactivateIndividual(ind.id);
                navigate("/caseload");
              }
            }} style={{
              width: "100%", marginTop: 24, padding: "12px 0", borderRadius: 12, fontSize: 12, fontWeight: 700,
              background: "rgba(252,129,129,0.06)", color: "#fc8181",
              border: "1px solid rgba(252,129,129,0.15)", cursor: "pointer",
            }}>
              Deactivate Individual
            </button>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Service Type Picker Modal */}
      {showServicePicker && ind && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-6">
          <div
            className="w-full max-w-sm rounded-2xl p-5"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(99,179,237,0.2)",
            }}
          >
            <h3 className="text-base font-bold text-gray-100 mb-1">Start Session</h3>
            <p className="text-xs text-gray-500 mb-4">
              Select the service type for {ind.firstName} {ind.lastName}
            </p>
            <div className="flex flex-col gap-2">
              {serviceOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setShowServicePicker(false);
                    navigate("/session/new/" + ind.id, { state: {
                      individualName: `${ind.lastName}, ${ind.firstName}`,
                      dob: ind.dob,
                      locationType: "Out-of-Clinic",
                      location: ind.address,
                      knownMedications: ind.medications || [],
                      knownDiagnoses: ind.diagnoses || [],
                      individualId: ind.id,
                      serviceType: opt.value,
                    }});
                  }}
                  className="w-full text-left py-3 px-4 rounded-xl text-sm"
                  style={{
                    background: "rgba(99,179,237,0.06)",
                    border: "1px solid rgba(99,179,237,0.12)",
                    color: "#e2e8f0",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{opt.label}</span>
                    <span className="text-[10px] text-gray-500 font-mono">{opt.code}</span>
                  </div>
                </button>
              ))}
              {serviceOptions.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-4">
                  No services configured. Go to Settings → Billing Configuration.
                </div>
              )}
            </div>
            <button
              onClick={() => setShowServicePicker(false)}
              className="w-full mt-3 py-2.5 rounded-xl text-xs font-semibold"
              style={{ color: "#718096" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}